import { useCallback, useEffect, useId, useMemo, useState, type ClipboardEvent as ReactClipboardEvent } from 'react';
import {
  SUPPORT_ATTACHMENT_MAX_COUNT,
  appendSupportImageFiles,
  captureScreen,
  captureVisiblePage,
  readClipboardImage,
  readClipboardImageFromEvent,
  validateSupportImageFileList,
} from '@wapharma/shared';
import { Button } from '../../primitives/Button.js';
import { FilePickerField, WP_IMAGE_FILE_ACCEPT, formatFileByteSizeFr } from '../../primitives/FilePickerField.js';
import { Group } from '../../primitives/Group.js';
import { Stack } from '../../primitives/Stack.js';
import { Text } from '../../primitives/Text.js';
import styles from './ImageAttachmentPicker.module.css';

export type ImageAttachmentPickerProps = {
  files: File[];
  onChange: (files: File[]) => void;
  onError?: (message: string) => void;
  variant?: 'full' | 'compact';
  onBeforePageCapture?: () => void | Promise<void>;
  onAfterPageCapture?: () => void | Promise<void>;
};

function previewUrl(file: File): string {
  return URL.createObjectURL(file);
}

export function ImageAttachmentPicker({
  files,
  onChange,
  onError,
  variant = 'full',
  onBeforePageCapture,
  onAfterPageCapture,
}: ImageAttachmentPickerProps) {
  const listId = useId();
  const [busy, setBusy] = useState<'screen' | 'page' | null>(null);
  const previews = useMemo(
    () => files.map((file) => ({ file, url: previewUrl(file) })),
    [files],
  );

  useEffect(
    () => () => {
      previews.forEach((p) => URL.revokeObjectURL(p.url));
    },
    [previews],
  );

  const addFiles = useCallback(
    (incoming: File[]) => {
      const next = appendSupportImageFiles(files, incoming);
      const check = validateSupportImageFileList(next);
      if (!check.ok) {
        onError?.(check.reason);
        return;
      }
      onChange(next);
    },
    [files, onChange, onError],
  );

  const onPick = useCallback(
    (picked: File | File[] | null) => {
      if (!picked) return;
      addFiles(Array.isArray(picked) ? picked : [picked]);
    },
    [addFiles],
  );

  const onPaste = useCallback(
    (event: ReactClipboardEvent) => {
      const file = readClipboardImageFromEvent(event.nativeEvent);
      if (!file) return;
      event.preventDefault();
      addFiles([file]);
    },
    [addFiles],
  );

  const onPasteButton = useCallback(async () => {
    const file = await readClipboardImage();
    if (file) addFiles([file]);
    else onError?.('Aucune image dans le presse-papiers.');
  }, [addFiles, onError]);

  const onScreenCapture = useCallback(async () => {
    setBusy('screen');
    try {
      const file = await captureScreen();
      if (file) addFiles([file]);
      else onError?.('Capture écran annulée ou indisponible.');
    } finally {
      setBusy(null);
    }
  }, [addFiles, onError]);

  const onPageCapture = useCallback(async () => {
    setBusy('page');
    try {
      await onBeforePageCapture?.();
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      const file = await captureVisiblePage();
      if (file) addFiles([file]);
      else onError?.('Capture page impossible.');
    } finally {
      await onAfterPageCapture?.();
      setBusy(null);
    }
  }, [addFiles, onAfterPageCapture, onBeforePageCapture, onError]);

  const removeAt = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const atLimit = files.length >= SUPPORT_ATTACHMENT_MAX_COUNT;

  return (
    <Stack
      gap="sm"
      className={variant === 'compact' ? styles.compact : undefined}
      onPaste={onPaste}
      tabIndex={0}
    >
      <Text size="xs" c="muted">
        {files.length}/{SUPPORT_ATTACHMENT_MAX_COUNT} image(s) — max 5 Mo chacune. Collez une capture avec Ctrl+V.
      </Text>

      {previews.length > 0 ? (
        <ul className={styles.thumbGrid} aria-labelledby={listId}>
          {previews.map((item, index) => (
            <li key={`${item.file.name}-${index}`} className={styles.thumbItem}>
              <img src={item.url} alt={item.file.name} className={styles.thumbImg} />
              <div className={styles.thumbMeta}>
                <Text size="xs" c="secondary" className={styles.thumbName}>
                  {item.file.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {formatFileByteSizeFr(item.file.size)}
                </Text>
              </div>
              <Button type="button" variant="ghost" size="xs" onClick={() => removeAt(index)}>
                Retirer
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <Text id={listId} size="sm" c="dimmed">
          Aucune pièce jointe (facultatif).
        </Text>
      )}

      <Group gap="xs" wrap="wrap">
        <FilePickerField
          accept={WP_IMAGE_FILE_ACCEPT}
          multiple
          chooseLabel="Ajouter une image"
          disabled={atLimit || busy !== null}
          onChange={(e) => {
            const list = e.currentTarget.files;
            if (!list?.length) return;
            onPick(Array.from(list));
            e.currentTarget.value = '';
          }}
        />
        <Button type="button" variant="soft" size="sm" disabled={atLimit || busy !== null} onClick={() => void onPasteButton()}>
          Coller
        </Button>
        <Button
          type="button"
          variant="soft"
          size="sm"
          disabled={atLimit || busy !== null}
          onClick={() => void onScreenCapture()}
        >
          {busy === 'screen' ? 'Capture…' : "Capturer l'écran"}
        </Button>
        <Button
          type="button"
          variant="soft"
          size="sm"
          disabled={atLimit || busy !== null}
          onClick={() => void onPageCapture()}
        >
          {busy === 'page' ? 'Capture…' : 'Capturer la page'}
        </Button>
      </Group>
    </Stack>
  );
}
